import type {
  CarePlan,
  CarePlanActivity,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { compact } from 'lodash-es'
import { match } from 'ts-pattern'
import {
  codeFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import { PRACTITIONER_ROLE } from '#src/codeSystems.ts'
import { BasePlan } from '#src/models/care-plans/BasePlan.ts'
import {
  CLOSED_CARE_PLAN_STATUSES,
  INTERVENTION_CARE_PLAN_CATEGORY,
} from '#src/models/care-plans/constants.ts'
import type { IDTRole } from '#src/models/care-teams/constants.ts'
import type { Outcome } from '#src/models/outcome.ts'
import { ROLES } from '#src/roles.ts'
import type { QueryTypes } from '#src/search.ts'

/* An intervention's care plan is its GRANDPARENT: an intervention is `partOf` a
 * goal care plan, which is `partOf` the initial care plan. `part-of:CarePlan.
 * part-of` is the two-hop chain the care plan page, the PDF and the RT desk node
 * already read interventions through (`byInitialCarePlan` below); this adds the
 * third hop onto that plan's status so the server does the filtering.
 *
 * Paired with `CLOSED_CARE_PLAN_STATUSES`, so it reads as "whose care plan is
 * not closed". An intervention the chain cannot walk to an initial care plan —
 * a dangling or deleted parent — does not match, which is the same answer every
 * other intervention read path in the app already gives: none of them can reach
 * it either. */
const OPEN_INITIAL_CARE_PLAN_PARAM =
  'part-of:CarePlan.part-of:CarePlan.status:not'

export class Intervention extends BasePlan {
  constructor({
    goalCarePlan,
    subject,
    outcome,
    title,
    order,
    ...rest
  }: Omit<CarePlan, 'resourceType' | 'intent' | 'status' | 'subject'> & {
    goalCarePlan: Reference<CarePlan>
    subject: Reference<Patient>
    outcome?: Outcome
    title?: string
    order: number
  }) {
    super({
      title,
      subject,
      category: [INTERVENTION_CARE_PLAN_CATEGORY],
      partOf: [goalCarePlan],
      goal: outcome ? [outcome.reference] : undefined,
      ...rest,
    })
    this.sortOrder = order
  }

  get activityReferences(): NonNullable<CarePlanActivity['reference']>[] {
    return compact(
      this.resource.activity?.map((activity) => activity.reference) ?? []
    )
  }

  set activityReferences(
    references: CarePlanActivity['reference'][] | undefined
  ) {
    this.resource.activity = references?.map((reference) => ({
      reference,
    }))
  }

  get author(): Reference<Practitioner> | undefined {
    return this.resource.author as Reference<Practitioner> | undefined
  }

  set author(value: Reference<Practitioner> | undefined) {
    this.resource.author = value
  }

  get idtRoles(): IDTRole[] {
    return (
      this.resource.category?.filter((c) =>
        c.coding?.some((coding) => coding.system === PRACTITIONER_ROLE)
      ) ?? []
    ).map((c) => codeFromCodeableConcept(c) as IDTRole)
  }

  set idtRoles(roles: IDTRole[]) {
    const existingCategories =
      this.resource.category?.filter(
        (c) => !c.coding?.some((coding) => coding.system === PRACTITIONER_ROLE)
      ) ?? []

    this.resource.category = [
      ...existingCategories,
      ...roles.map((role) => ROLES[role]),
    ]
  }

  get isInactive(): boolean {
    return (
      this.status === 'completed' ||
      this.status === 'revoked' ||
      this.status === 'entered-in-error'
    )
  }

  /* A new intervention is stored as `draft`. The status picker labels that
   * "Active" and, when someone chooses the option that already matches the
   * label, writes `active`. Those two are the same in-progress intervention.
   * Completed, discontinued, and the signed-off snapshot (`on-hold`) stay
   * locked. The care-plan page still gates the Active case on
   * `intervention-active-editable`. */
  override get isEditable(): boolean {
    return this.status === 'draft' || this.status === 'active'
  }

  get goalCarePlan(): Reference<CarePlan> | undefined {
    return this.resource.partOf?.[0] as Reference<CarePlan> | undefined
  }

  static statusText(status: Intervention['status']): string {
    return match(status)
      .with('completed', () => 'Completed')
      .with('revoked', () => 'Discontinued')
      .with('entered-in-error', () => 'Deleted')
      .otherwise(() => 'Active')
  }

  static byGoalCarePlan(params: {
    goalCarePlan?: Reference<CarePlan>
  }): QueryTypes {
    return {
      'part-of': params.goalCarePlan,
      // _sort: 'date',
    }
  }

  static byOutcome(params: {
    goalCarePlan?: Reference<CarePlan>
    outcome?: Outcome
  }): QueryTypes {
    return {
      ...this.byGoalCarePlan({
        goalCarePlan: params.goalCarePlan,
      }),
      goal: params.outcome?.reference,
    }
  }

  static queryOpenByParticipant(subject: Reference<Patient>): QueryTypes {
    return {
      category: INTERVENTION_CARE_PLAN_CATEGORY,
      subject,
      'status:not': CLOSED_CARE_PLAN_STATUSES,
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  /* Every intervention a note may mention for this participant.
   *
   * Two different statuses are being asked about, and they pull opposite ways.
   *
   * The intervention's OWN status stays wide: a note documenting why an
   * intervention was completed or discontinued has to be able to name it, so
   * only `entered-in-error` — the deleted state, which no longer describes care
   * that happened — is withheld.
   *
   * Its CARE PLAN's status narrows: once a plan is closed it is last cycle's
   * care, and its interventions are not what the note being written is about.
   * Activating a plan closes the one it replaces (`activateCarePlan`) without
   * touching any intervention underneath, so the old cycle's interventions keep
   * whatever status they had and only the plan records that the cycle ended.
   * Reading the intervention's own status alone cannot see that, which is why
   * the filter has to reach up the tree. */
  static queryMentionableByParticipant(
    subject: Reference<Patient>
  ): QueryTypes {
    return {
      category: INTERVENTION_CARE_PLAN_CATEGORY,
      subject,
      'status:not': ['entered-in-error'],
      [OPEN_INITIAL_CARE_PLAN_PARAM]: CLOSED_CARE_PLAN_STATUSES,
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static byInitialCarePlan({
    initialCarePlan,
  }: {
    initialCarePlan?: Reference<CarePlan>
  }): QueryTypes {
    return {
      'part-of:CarePlan.part-of': initialCarePlan,
      'status:not': ['entered-in-error'],
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static isInstance(carePlan: BasePlan): carePlan is BasePlan {
    return hasCodeableConcept(
      carePlan.categories,
      INTERVENTION_CARE_PLAN_CATEGORY
    )
  }
}
